import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import DeliveryTipsSelectButton from './DeliveryTipsSelectButton';
import DeliveryTipsCustomAmount from './DeliveryTipsCustomAmount';
import { formatPrice } from '../../util/format';
import Icon from '../Icon';
import { logAmplitudeEvent } from '../../services/amplitude';

const DeliveryTips = (props) => {
  const PERCENTAGE_DEFAULTS = [0.05, 0.1, 0.15];
  const DOLLAR_DEFAULTS = [1, 3, 5];

  const { subTotal, selectedCustomTipValue, addDeliveryTip } = props;
  const isDollar = subTotal <= 25;
  const BUTTON_DEFAULTS = isDollar
    ? DOLLAR_DEFAULTS
    : PERCENTAGE_DEFAULTS.map((percentage) => +(percentage * subTotal).toFixed(2));

  const [isCustom, setIsCustom] = useState(selectedCustomTipValue > 0);
  const [previousCustom, setPreviousCustom] = useState(0);
  const [animatingButtonId, setAnimatingButtonId] = useState(null);

  const onClickHandler = (e) => {
    addDeliveryTip('tipAmount', e.target.value);
    setIsCustom(false);
    setAnimatingButtonId(e.target.id);
    logAmplitudeEvent('[My Payment] Tip Button Clicked', { value: e.target.value });
  };

  const onAnimationComplete = () => setAnimatingButtonId(null);

  const beforeInputHandler = (e) => {
    setPreviousCustom(e.target.value);
  };

  const onInputHandler = (e) => {
    if (e.target.value > 9999) {
      e.target.value = previousCustom;
    }
    if (
      e.target.value.toString().split('.').length > 1 &&
      e.target.value.toString().split('.')[1].length > 2
    ) {
      e.target.value = parseFloat(e.target.value).toFixed(2);
    }
  };

  const onKeyDownHandler = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  };

  const onBlurHandler = (e) => {
    e.preventDefault();
    if (e.target.value !== '') {
      addDeliveryTip('tipAmount', e.target.value);
      logAmplitudeEvent('[My Payment] Custom Tip Entered', { value: e.target.value });
    }
  };

  return (
    <StyledDeliveryTips className="styled-delivery-tips">
      <div className="delivery-tip-intro">Tip amount</div>
      <div className="delivery-tip-intro-sub">100% of tips will go to Team Members</div>
      <div className="flex-wrap-container">
        <div className="tip-container">
          <DeliveryTipsSelectButton
            tipPercentageButtonId="button-no-tip"
            onClickHandler={onClickHandler}
            checked={!isCustom && selectedCustomTipValue === 0}
            className="select-tip-button"
            labelText="No Tip"
            value={0}
          >
            <Icon>line</Icon>
          </DeliveryTipsSelectButton>
        </div>
        {BUTTON_DEFAULTS.map((amount, index) => (
          <div className="tip-container" key={amount}>
            <DeliveryTipsSelectButton
              tipPercentageButtonId={`tip-${amount}`.replace('.', '')}
              onClickHandler={onClickHandler}
              checked={!isCustom && selectedCustomTipValue === amount}
              className="select-tip-button"
              labelText={`${formatPrice(isDollar ? amount + subTotal : amount)}`}
              value={amount}
              isAnimating={animatingButtonId === `tip-${amount}`.replace('.', '')}
              onAnimationComplete={onAnimationComplete}
            >
              {isDollar ? `$${amount}` : `${PERCENTAGE_DEFAULTS[index] * 100}%`}
            </DeliveryTipsSelectButton>
          </div>
        ))}
        <div className="tip-container">
          <DeliveryTipsSelectButton
            tipPercentageButtonId="button-custom-tip"
            onClickHandler={() => {
              setIsCustom(true);
              addDeliveryTip('tipAmount', 0);
              setAnimatingButtonId('button-custom-tip');
            }}
            checked={isCustom}
            className="select-tip-button"
            labelText="Custom"
            value={-1}
            isAnimating={animatingButtonId === 'button-custom-tip'}
            onAnimationComplete={onAnimationComplete}
          >
            <Icon>edit</Icon>
          </DeliveryTipsSelectButton>
        </div>
        {isCustom && (
          <div className="custom-tip-container">
            <DeliveryTipsCustomAmount
              isSelect={isCustom}
              onBlurHandler={onBlurHandler}
              onInputHandler={onInputHandler}
              beforeInputHandler={beforeInputHandler}
              onKeyDownHandler={onKeyDownHandler}
              selectedCustomTipValue={selectedCustomTipValue}
            />
          </div>
        )}
      </div>
    </StyledDeliveryTips>
  );
};

const StyledDeliveryTips = styled.div`
  padding: 16px;
  background: ${(props) => props.theme.background};
  border-radius: 10px;
  & .delivery-tip-intro {
    color: ${(props) => props.theme.bold};
    font-size: 14px;
    font-weight: 700;
    margin: 0 0 5px;
  }
  & .delivery-tip-intro-sub {
    color: ${(props) => props.theme.text};
    font-size: 14px;
    font-weight: 400;
    margin: 0 0 5px;
  }
  .flex-wrap-container {
    display: flex;
    flex-wrap: wrap;
  }
  & .tip-container {
    width: 50%;
  }
  @media screen and (min-width: 40em) {
    & .tip-container {
      width: 20%;
    }
  }
  & .custom-tip-container {
    width: 100%;
    padding: 0 5px;
  }
`;

DeliveryTips.propTypes = {
  subTotal: PropTypes.number,
  selectedCustomTipValue: PropTypes.number,
  addDeliveryTip: PropTypes.func,
};

DeliveryTips.defaultProps = {
  subTotal: 0,
  selectedCustomTipValue: 0,
  addDeliveryTip: () => {},
};

export default DeliveryTips;
